export class ApiError extends Error {
    status;
    code;
    fields;
    incidentId;
    conflict;
    locked;
    retryAfter;
    withdrawn;
    ticketId;
    constructor(status, code, message, 
    /** Feldbezogene Befunde; die API deckelt sie (höchstens 50, je höchstens 300 Zeichen). */
    fields, incidentId, cause, 
    /** Nur bei 409 gefüllt: der aktuelle Stand des Datensatzes auf dem Server. */
    conflict, 
    /** Nur bei 423 gefüllt: wer die Sperre hält und seit wann. */
    locked, 
    /** Nur bei 429 gefüllt: Sekunden bis zum nächsten erlaubten Versuch (Kopfzeile `Retry-After`, sonst
     *  `wartenSekunden` im Rumpf) — etwa „Jetzt abgleichen" der Fremdtickets (F141). */
    retryAfter, 
    /** Nur bei 410 einer zurückgenommenen Teilaufgabe (Spec Teilaufgaben R14): ihr Elternticket. */
    withdrawn, 
    /** Nur bei 409 `TICKET_DUE_ORIGIN_TAKEN` und nur bei Sicht gefüllt: das vorhandene Ticket der Fälligkeit. */
    ticketId) {
        super(message, cause !== undefined ? { cause } : undefined);
        this.status = status;
        this.code = code;
        this.fields = fields;
        this.incidentId = incidentId;
        this.conflict = conflict;
        this.locked = locked;
        this.retryAfter = retryAfter;
        this.withdrawn = withdrawn;
        this.ticketId = ticketId;
        this.name = "ApiError";
    }
}
/**
 * Gemeinsame Fehlerauswertung für hole(), sende() und sendeUndLies(). Bis Aufgabe 12 stand
 * dieselbe Auswertung zweimal wortgleich in hole() und sende() — eine bewusste, terminierte
 * Doppelung aus Aufgabe 2 (siehe dortige Entscheidung), jetzt hier zusammengezogen.
 */
/** Wartezeit einer 429 in Sekunden: `Retry-After` (nur die Sekundenform), sonst `wartenSekunden` im Rumpf. */
function wartezeit(antwort, koerper) {
    const kopf = Number(antwort.headers.get("retry-after") ?? Number.NaN);
    if (Number.isInteger(kopf) && kopf > 0)
        return kopf;
    const rumpf = koerper?.wartenSekunden;
    return typeof rumpf === "number" && Number.isInteger(rumpf) && rumpf > 0 ? rumpf : undefined;
}
async function wirfBeiFehler(antwort) {
    if (antwort.ok)
        return;
    let koerper = null;
    try {
        koerper = (await antwort.json());
    }
    catch { /* kein JSON */ }
    const konflikt = antwort.status === 409 && koerper?.changedBy !== undefined
        ? { changedBy: koerper.changedBy, changedAt: koerper.changedAt ?? "", current: koerper.current }
        : undefined;
    const gesperrt = antwort.status === 423 && koerper?.lockedBy !== undefined
        ? { lockedBy: koerper.lockedBy, since: koerper.since ?? "" }
        : undefined;
    const r = koerper;
    const zurueckgenommen = antwort.status === 410 && typeof r?.parentId === "string"
        ? { parentId: r.parentId, parentTicketNumber: String(r.parentTicketNumber ?? ""), parentTitle: String(r.parentTitle ?? "") }
        : undefined;
    throw new ApiError(antwort.status, koerper?.code ?? "UNBEKANNT", koerper?.message ?? `Die Anfrage schlug fehl (${antwort.status}).`, koerper?.fields, koerper?.incidentId, undefined, konflikt, gesperrt, antwort.status === 429 ? wartezeit(antwort, koerper) : undefined, zurueckgenommen, antwort.status === 409 && koerper?.code === "TICKET_DUE_ORIGIN_TAKEN" && typeof r?.ticketId === "string" ? r.ticketId : undefined);
}
const networkError = (ursprung) => new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
/** `signal` bricht den Abruf ab (überholte Anfragen). Ein Abbruch endet als `NETZWERKFEHLER`, auch mitten im Rumpf einer
 *  Erfolgsantwort (K34); bricht das Lesen eines Fehlerrumpfs ab, bleibt der echte Status mit Code `UNBEKANNT`. */
async function hole(v, pfad, signal) {
    let antwort;
    try {
        antwort = await fetch(`${v.basis}${pfad}`, {
            credentials: "include",
            headers: { accept: "application/json", ...v.kopf() },
            ...(signal === undefined ? {} : { signal }),
        });
    }
    catch (ursprung) {
        // fetch selbst wirft z. B. bei Verbindungsverweigerung oder fehlendem Netz —
        // das darf die Oberfläche nicht als rohen TypeError zu sehen bekommen.
        throw networkError(ursprung);
    }
    await wirfBeiFehler(antwort);
    try {
        return (await antwort.json());
    }
    catch (ursprung) {
        if (signal?.aborted === true)
            throw networkError(ursprung);
        // Eine 200er-Antwort mit kaputtem Inhalt (z. B. durch einen fehlerhaften
        // Zwischenserver) soll ebenfalls als ApiError ankommen, nicht als roher SyntaxError.
        throw new ApiError(antwort.status, "UNGUELTIGES_JSON", "Die Antwort des Servers konnte nicht gelesen werden.", undefined, undefined, ursprung);
    }
}
/**
 * Die Anfrage-Vorgaben für einen Schreibweg — an EINER Stelle für `sende` und `sendeUndLies`.
 *
 * **Ohne Rumpf kein `content-type`.** Das ist keine Kosmetik: Fastify 5 weist ein DELETE mit
 * `content-type: application/json`, aber leerem Rumpf mit 400 ab
 * (`FST_ERR_CTP_EMPTY_JSON_BODY`) — noch vor dem Handler. Bis Befund W1 der Prüfung von
 * Aufgabe 3 setzten beide Funktionen den Kopf pauschal, und damit funktionierte KEIN einziger
 * Löschweg dieses Clients gegen einen echten Server (`deleteNote`, `deleteImportantNote`,
 * `deleteFile`, `deleteFolder`, `deleteTextBlock`, `deleteOfferTemplate`, `deleteItem`).
 * Gemerkt hat es niemand, weil die GESTALT-Tabelle `fetch` fälscht — die Gegenproben stehen
 * deshalb in `client.test.ts` an einem ECHTEN Fastify über eine echte Leitung.
 *
 * `JSON.stringify(undefined)` ist `undefined`; `fetch` sendet dann keinen Rumpf. Ein Aufrufer,
 * der wirklich `null` schicken will, übergibt `null` — das ist gültiges JSON und bekommt
 * folgerichtig auch den Kopf.
 */
function schreibVorgaben(methode, koerper, kopf) {
    const rumpf = koerper === undefined ? undefined : JSON.stringify(koerper);
    return {
        method: methode,
        credentials: "include",
        headers: rumpf === undefined
            ? { accept: "application/json", ...kopf }
            : { accept: "application/json", "content-type": "application/json", ...kopf },
        ...(rumpf === undefined ? {} : { body: rumpf }),
    };
}
async function sende(v, pfad, methode, koerper) {
    let antwort;
    try {
        antwort = await fetch(`${v.basis}${pfad}`, schreibVorgaben(methode, koerper, v.kopf()));
    }
    catch (ursprung) {
        // Dieselbe Behandlung wie bei hole(): fetch selbst wirft z. B. bei
        // Verbindungsverweigerung oder fehlendem Netz.
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
}
/**
 * Wie sende(), aber für Schreibwege MIT Antwortkörper (Anlegen, Ändern, Sperre erwerben/
 * erneuern, Position löschen). Dieselbe Netzwerk- und Fehlerbehandlung wie hole() und sende()
 * — und dieselben Anfrage-Vorgaben, also auch hier ohne Rumpf kein `content-type`; zusätzlich
 * wird die erfolgreiche Antwort gelesen, mit derselben Absicherung gegen kaputtes JSON.
 */
/**
 * Wie `sendeUndLies`, gibt aber den ANTWORTCODE mit zurück.
 *
 * Gebraucht an genau einem Weg: „Korrekturrechnung erstellen" antwortet mit 201, wenn sie
 * eben entstanden ist, und mit 200, wenn es sie schon gab (`korrektur-erzeugen`). Beide
 * Male steht dieselbe Rechnung im Rumpf — der Unterschied steckt AUSSCHLIESSLICH im Code,
 * und ohne ihn müsste die Maske ihn aus den Daten erraten.
 *
 * `sendeUndLies` bleibt die Vorgabe für alle übrigen Wege und ruft diese Funktion auf: Zwei
 * Fassungen derselben Fehlerauswertung liefen unweigerlich auseinander.
 */
async function sendeUndLiesMitCode(v, pfad, methode, koerper, signal) {
    let antwort;
    try {
        antwort = await fetch(`${v.basis}${pfad}`, { ...schreibVorgaben(methode, koerper, v.kopf()), ...(signal === undefined ? {} : { signal }) });
    }
    catch (ursprung) {
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
    try {
        return { status: antwort.status, daten: (await antwort.json()) };
    }
    catch (ursprung) {
        throw new ApiError(antwort.status, "UNGUELTIGES_JSON", "Die Antwort des Servers konnte nicht gelesen werden.", undefined, undefined, ursprung);
    }
}
async function sendeUndLies(v, pfad, methode, koerper, signal) {
    // Der Antwortcode interessiert hier nicht — nur ein Weg unterscheidet 200 von 201
    // (siehe `sendeUndLiesMitCode`), und die Fehlerauswertung soll es nur einmal geben.
    return (await sendeUndLiesMitCode(v, pfad, methode, koerper, signal)).daten;
}
/** So lange wartet eine Schreibung der Kachelanordnung höchstens (siehe `saveTileLayout`). */
const TILE_LAYOUT_TIMEOUT_MS = 15_000;
/** Ruft `send` mit einem Signal, das nach `ms` abbricht: `fetch` scheitert dann wie ohne Netz (`NETZWERKFEHLER`). */
async function withTimeout(ms, send) {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), ms);
    try {
        return await send(abort.signal);
    }
    finally {
        clearTimeout(timer);
    }
}
/**
 * Wie sendeUndLies(), aber für einen Datei-Upload als multipart/form-data. Bewusst OHNE einen
 * eigenen content-type-Kopf: Bei einem FormData-Rumpf setzt fetch den Kopf selbst — samt der
 * `boundary`, die den Rumpf strukturiert. Ein von Hand gesetzter content-type (wie bei den
 * JSON-Methoden) verlöre die boundary und machte den Rumpf unlesbar. Sonst dieselbe
 * Netzwerk- und Fehlerbehandlung.
 */
async function sendeFormular(v, pfad, form, methode = "POST") {
    let antwort;
    try {
        antwort = await fetch(`${v.basis}${pfad}`, {
            // PUT seit der Unterschrift am Benutzer, Layout-Editor Etappe 1.
            method: methode,
            credentials: "include",
            headers: { accept: "application/json", ...v.kopf() },
            body: form,
        });
    }
    catch (ursprung) {
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
    try {
        return (await antwort.json());
    }
    catch (ursprung) {
        throw new ApiError(antwort.status, "UNGUELTIGES_JSON", "Die Antwort des Servers konnte nicht gelesen werden.", undefined, undefined, ursprung);
    }
}
/** Zieht den Dateinamen aus einem Content-Disposition-Kopf — bevorzugt den verlustfreien
 *  RFC-5987-Teil (`filename*=UTF-8''…`), sonst den ASCII-Rückfall (`filename="…"`). `null`,
 *  wenn keiner da ist. */
function dateinameAusKopf(kopf) {
    if (kopf === null)
        return null;
    const stern = /filename\*=UTF-8''([^;]+)/i.exec(kopf);
    if (stern !== null) {
        try {
            return decodeURIComponent(stern[1]);
        }
        catch { /* fällt auf ascii zurück */ }
    }
    const ascii = /filename="([^"]*)"/i.exec(kopf);
    return ascii !== null ? ascii[1] : null;
}
/**
 * Holt eine Binärdatei (Download) mit derselben Netzwerk-/Fehlerbehandlung wie hole(), gibt aber
 * die rohen Bytes als Blob zurück — plus den ausgelieferten Dateinamen aus dem
 * Content-Disposition-Kopf (den die Route IMMER als attachment setzt). Ohne accept:json, weil
 * die Antwort kein JSON ist.
 */
async function holeBinaer(v, pfad) {
    let antwort;
    try {
        antwort = await fetch(`${v.basis}${pfad}`, { credentials: "include", headers: v.kopf() });
    }
    catch (ursprung) {
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
    const blob = await antwort.blob();
    return { blob, filename: dateinameAusKopf(antwort.headers.get("content-disposition")) };
}
/**
 * Wie holeBinaer(), aber als rumpfloses POST — die Rendern-Wege (E3-T4) LÖSEN etwas aus
 * (die Engine setzt den Beleg) und sind deshalb POSTs, ihre Antwort ist aber derselbe
 * Download wie bei einer Datei. Ohne Rumpf KEIN content-type (Befund W1: Fastify 5 weist
 * ein leeres JSON-POST mit gesetztem Kopf ab) und ohne accept:json — die Antwort ist PDF.
 */
async function sendeBinaer(v, pfad) {
    let antwort;
    try {
        antwort = await fetch(`${v.basis}${pfad}`, { method: "POST", credentials: "include", headers: v.kopf() });
    }
    catch (ursprung) {
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
    const blob = await antwort.blob();
    return { blob, filename: dateinameAusKopf(antwort.headers.get("content-disposition")) };
}
/** Der Verwaltungspfad einer Logo-Variante (Hochladen und Löschen) — an einer Stelle,
 *  damit die beiden Methoden nicht auseinanderlaufen können. Die Variante ist getippt und
 *  braucht keine Kodierung; die Kennung schon. */
function verwaltungsLogoPfad(id, variante) {
    return `/api/verwaltung/unternehmen/${encodeURIComponent(id)}/logo/${variante}`;
}
/** Der Pfad einer Beziehung am Kunden (Rollen- und Kreditorweg). */
function relationPath(customerId, relationId) {
    return `/api/customers/${encodeURIComponent(customerId)}/relations/${encodeURIComponent(relationId)}`;
}
/** Der Pfad der Lieferverträge eines Lieferanten, optional eines bestimmten. */
function contractPath(customerId, contractId) {
    return `/api/customers/${encodeURIComponent(customerId)}/supplier-contracts`
        + (contractId === undefined ? "" : `/${encodeURIComponent(contractId)}`);
}
/** Sicht, Status und Art wiederholt (`?companyId=a&state=RUNNING&typeKey=TELECOM`), „Kündigungsfrist bis“ nur gesetzt. */
function contractAreaQuery(q) {
    const p = new URLSearchParams();
    unternehmenAnhaengen(p, q.companyIds);
    for (const state of q.states ?? [])
        p.append("state", state);
    for (const typeKey of q.typeKeys ?? [])
        p.append("typeKey", typeKey);
    if (q.noticeUntil !== undefined)
        p.set("noticeUntil", q.noticeUntil);
    const query = p.toString();
    return query === "" ? "" : `?${query}`;
}
/** Die Unternehmen einer Sicht als wiederholter Parameter (Spec Mandantentrennung, R6) — fehlt die
 *  Liste, schickt der Klient nichts, und die Regel des Wegs gilt (Sichten: das aktive Unternehmen, Cockpit: alle sichtbaren). */
function unternehmenAnhaengen(p, ids) {
    for (const id of ids ?? [])
        if (id !== "")
            p.append("companyId", id);
}
function sichtAnhang(ids) {
    const p = new URLSearchParams();
    unternehmenAnhaengen(p, ids);
    const anhang = p.toString();
    return anhang === "" ? "" : `?${anhang}`;
}
/** Nur gesetzte Angaben; Sicht und `override` wiederholt (`?companyId=a&companyId=b&override=…`). */
function liquiditySeriesAnhang(q) {
    const p = new URLSearchParams();
    for (const name of ["granularity", "scenario", "compare", "from", "to"]) {
        const wert = q[name];
        if (wert !== undefined)
            p.set(name, wert);
    }
    unternehmenAnhaengen(p, q.companyIds);
    for (const override of q.overrides ?? [])
        p.append("override", override);
    const anhang = p.toString();
    return anhang === "" ? "" : `?${anhang}`;
}
function planningItemsQuery(q) {
    const p = new URLSearchParams();
    if (q.scenarioId !== undefined)
        p.set("scenarioId", q.scenarioId);
    unternehmenAnhaengen(p, q.companyIds);
    const anhang = p.toString();
    return anhang === "" ? "" : `?${anhang}`;
}
/** Baut die Abfragezeichenkette der Rechnungsliste. Eigene Funktion und nicht
 *  `vorgangsSucheAnhang` mit einem Sonderzweig: Der Statusfilter ist hier WIEDERHOLBAR
 *  (`?status=ISSUED&status=PAID`), und ein Helfer, der beides kann, könnte beides auch
 *  verwechseln. */
function rechnungsSucheAnhang(q) {
    const p = new URLSearchParams();
    unternehmenAnhaengen(p, q.companyIds);
    for (const s of q.status ?? [])
        p.append("status", s);
    if (q.suche !== undefined && q.suche !== "")
        p.set("suche", q.suche);
    if (q.limit !== undefined)
        p.set("limit", String(q.limit));
    if (q.offset !== undefined)
        p.set("offset", String(q.offset));
    const anhang = p.toString();
    return anhang === "" ? "" : `?${anhang}`;
}
/** Eine Abfragezeichenkette aus GESETZTEN Angaben (Zahlen, Wahrheitswerte, Texte); `undefined` fällt weg. */
function abfrage(q) {
    const p = new URLSearchParams();
    for (const [name, wert] of Object.entries(q))
        if (wert !== undefined)
            p.set(name, String(wert));
    const anhang = p.toString();
    return anhang === "" ? "" : `?${anhang}`;
}
/** Die Abfragezeichenkette der Klärliste — nur GESETZTE Angaben landen darin (Bauart wie
 *  `rechnungsSucheAnhang`). `alle: false` wird weggelassen: Es ist die Vorgabe des Servers,
 *  und eine Zeichenkette, die Vorgaben wiederholt, veraltet mit der ersten Änderung. */
function klaerlisteAnhang(q) {
    const p = new URLSearchParams();
    if (q.alle === true)
        p.set("alle", "true");
    if (q.suche !== undefined && q.suche !== "")
        p.set("suche", q.suche);
    if (q.limit !== undefined)
        p.set("limit", String(q.limit));
    if (q.offset !== undefined)
        p.set("offset", String(q.offset));
    const anhang = p.toString();
    return anhang === "" ? "" : `?${anhang}`;
}
/** Baut die Abfragezeichenkette beider globaler Listen — an EINER Stelle, weil beide
 *  dieselben fünf Filter tragen und ein Auseinanderlaufen genau die Sorte Fehler wäre, die
 *  erst an der zweiten Maske auffällt. Bauart wie bei `searchProducts`: nur GESETZTE Filter
 *  landen in der Zeichenkette. */
function vorgangsSucheAnhang(q) {
    const p = new URLSearchParams();
    unternehmenAnhaengen(p, q.companyIds);
    if (q.status !== undefined && q.status !== "")
        p.set("status", q.status);
    if (q.kind !== undefined)
        p.set("kind", q.kind);
    if (q.suche !== undefined && q.suche !== "")
        p.set("suche", q.suche);
    if (q.limit !== undefined)
        p.set("limit", String(q.limit));
    if (q.offset !== undefined)
        p.set("offset", String(q.offset));
    const anhang = p.toString();
    return anhang === "" ? "" : `?${anhang}`;
}
/** Der Dateipfad eines Vorgangs, optional mit Datei — an EINER Stelle für alle vier Dateiwege. */
function vorgangsDateiPfad(customerId, ref, fileId) {
    return `/api/customers/${encodeURIComponent(customerId)}/${ref.art}/${encodeURIComponent(ref.id)}/files`
        + (fileId === undefined ? "" : `/${encodeURIComponent(fileId)}`);
}
/** Das Pfadsegment zur Vorgangsart. Feste Zeichenketten aus dieser Zuordnung, nie aus einer
 *  Eingabe zusammengesetzt — dieselbe Bauart wie im Repository und in den Routen. */
const VORGANGS_SEGMENT = {
    offer: "offers",
    order: "orders",
    invoice: "invoices",
};
/** Der Pfad zu EINER Rechnung — an einer Stelle für alle sieben Rechnungswege. */
function rechnungsPfad(customerId, invoiceId) {
    return `/api/customers/${encodeURIComponent(customerId)}/invoices/`
        + `${encodeURIComponent(invoiceId)}`;
}
/**
 * Der Rumpf einer Vorschau-Anfrage: `{ dpi }` oder ein LEERES Objekt.
 *
 * Ein leeres Objekt und nicht `undefined`: Die Route hat ein Rumpfschema, und Fastify 5
 * weist ein POST mit gesetztem content-type und leerem Rumpf ab (Befund W1 an den
 * Rendern-Wegen). `{ dpi: undefined }` wäre ausserdem irreführend — in JSON verschwindet
 * der Schlüssel, aber im Code sähe es aus, als würde etwas geschickt.
 */
function vorschauRumpf(dpi) {
    return dpi === undefined ? {} : { dpi };
}
/**
 * Der Bezug als STABILE Zeichenkette — für die Abhängigkeitsliste eines Effekts.
 *
 * Ein Bezug ist ein Objektliteral, und ein Literal im JSX ist bei jedem Zeichnen ein neues:
 * Ein Effekt, der am OBJEKT hängt, lädt nach jedem Zeichnen neu. Gebraucht wird also der
 * INHALT, und zwar in derselben Form an jeder Stelle — der Positions-Editor und die
 * Zahlplan-Tafel hatten die Bildung zweimal abgeschrieben, und zwei Abschriften derselben
 * Regel laufen beim nächsten Bezugsfall auseinander.
 *
 * Er steht HIER und nicht in `web`, weil er zum Typ `Positionsbezug` gehört: Wer den
 * Summentyp erweitert, sieht seinen Schlüssel eine Zeile weiter und vergisst ihn nicht.
 * Die Zeichenkette ist KEIN Pfad und keine Kennung — sie wird nie verschickt, sie
 * unterscheidet nur.
 */
export function bezugsSchluessel(bezug) {
    return bezug.art === "template"
        ? `template:${bezug.vorlageId}`
        : `${bezug.art}:${bezug.customerId}:${bezug.vorgangId}`;
}
/** Der Pfad zu den Positionen — an EINER Stelle für alle sechs Wege und alle VIER
 *  Familien. */
function positionenPfad(bezug) {
    if (bezug.art === "template") {
        // Die Vorlagenfamilie steht unter /api/verwaltung (ADMIN) und trägt nur EINE Kennung.
        return `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(bezug.vorlageId)}`
            + "/positionen";
    }
    return `/api/customers/${encodeURIComponent(bezug.customerId)}/`
        + `${VORGANGS_SEGMENT[bezug.art]}/${encodeURIComponent(bezug.vorgangId)}/positionen`;
}
/** Der Parteien-Pfad eines Vorgangs (Adressen Etappe 3) — an EINER Stelle für alle sechs
 *  Wege (`registriereParteienFamilie` in `routes/belegParteien.ts`). */
function parteienPfad(customerId, art, vorgangId) {
    return `/api/customers/${encodeURIComponent(customerId)}/${VORGANGS_SEGMENT[art]}/`
        + `${encodeURIComponent(vorgangId)}/parteien`;
}
/** Der Pfad der Layoutwahl eines Belegs (Migration 049) — `registriereLayoutFamilie` in `routes/belegLayout.ts`. */
function belegLayoutPfad(customerId, art, vorgangId) {
    return `/api/customers/${encodeURIComponent(customerId)}/${VORGANGS_SEGMENT[art]}/`
        + `${encodeURIComponent(vorgangId)}/layout`;
}
/** Der Zahlplan-Pfad — an EINER Stelle für alle DREI Familien
 *  (`registriereZahlplanFamilie` in `routes/zahlplan.ts`). */
function zahlplanPfad(bezug) {
    if (bezug.art === "template") {
        return `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(bezug.vorlageId)}`
            + "/zahlplan";
    }
    return `/api/customers/${encodeURIComponent(bezug.customerId)}/`
        + `${VORGANGS_SEGMENT[bezug.art]}/${encodeURIComponent(bezug.vorgangId)}/zahlplan`;
}
/** Der Anhangs-Pfad — an EINER Stelle für alle drei Familien
 *  (`registriereAnhangsFamilie` in `routes/anhaenge.ts`). */
function anhangPfad(bezug) {
    if (bezug.art === "template") {
        return `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(bezug.vorlageId)}`
            + "/anhaenge";
    }
    const segment = bezug.art === "offer" ? "offers" : "orders";
    return `/api/customers/${encodeURIComponent(bezug.customerId)}/`
        + `${segment}/${encodeURIComponent(bezug.vorgangId)}/anhaenge`;
}
/** Der Bezug als STABILE Zeichenkette — für die Abhängigkeitsliste eines Effekts
 *  (Muster `bezugsSchluessel`; ein Objektliteral im JSX ist bei jedem Zeichnen ein neues,
 *  und ein Effekt, der am OBJEKT hängt, lädt nach jedem Zeichnen neu). */
export function anhangsSchluessel(bezug) {
    return bezug.art === "template"
        ? `template:${bezug.vorlageId}`
        : `${bezug.art}:${bezug.customerId}:${bezug.vorgangId}`;
}
function ansprechpartnerPfad(b) {
    return `/api/customers/${encodeURIComponent(b.customerId)}/${b.art === "offer" ? "offers" : "orders"}/`
        + `${encodeURIComponent(b.vorgangId)}/ansprechpartner`;
}
const KATEGORIEN_PFAD = "/api/verwaltung/ansprechpartner-kategorien";
/** Der Fälligkeiten-Pfad — nur am AUFTRAG (die Planung rechnet nur mit ihm). */
function faelligkeitenPfad(customerId, orderId) {
    return `/api/customers/${encodeURIComponent(customerId)}/orders/`
        + `${encodeURIComponent(orderId)}/faelligkeiten`;
}
/** Die Teilrechnungs-Wege — nur am AUFTRAG (ein Angebot hat keine Rechnungen). */
function auftragsWeg(customerId, orderId, weg) {
    return `/api/customers/${encodeURIComponent(customerId)}/orders/`
        + `${encodeURIComponent(orderId)}/${weg}`;
}
/** Der Listen-/Anlege-Pfad einer Adressfamilie — an EINER Stelle für alle drei Besitzerarten
 *  (`registriereAdressFamilie` in `routes/addresses.ts`). */
function adressWeg(besitzer, ownerId) {
    return `/api/${besitzer}/${encodeURIComponent(ownerId)}/addresses`;
}
/** Der Pfad der Eingangsrechnungen eines Lieferanten, optional einer bestimmten. */
function incomingPath(customerId, incomingInvoiceId) {
    return `/api/customers/${encodeURIComponent(customerId)}/incoming-invoices`
        + (incomingInvoiceId === undefined ? "" : `/${encodeURIComponent(incomingInvoiceId)}`);
}
/** Sicht und Zahlstatus wiederholt (`?companyId=a&state=OVERDUE&state=DUE_SOON`), die übrigen Filter nur gesetzt. */
function incomingAreaQuery(q) {
    const p = new URLSearchParams();
    unternehmenAnhaengen(p, q.companyIds);
    for (const state of q.states ?? [])
        p.append("state", state);
    if (q.dueUntil !== undefined)
        p.set("dueUntil", q.dueUntil);
    if (q.customerId !== undefined)
        p.set("customerId", q.customerId);
    if (q.withoutAccount !== undefined)
        p.set("withoutAccount", String(q.withoutAccount));
    const query = p.toString();
    return query === "" ? "" : `?${query}`;
}
function cockpitTileQuery(q) {
    const p = new URLSearchParams();
    if (q.from !== undefined)
        p.set("from", q.from);
    if (q.to !== undefined)
        p.set("to", q.to);
    unternehmenAnhaengen(p, q.companyIds);
    if (q.compare !== undefined)
        p.set("compare", q.compare);
    if (q.align !== undefined)
        p.set("align", q.align);
    const query = p.toString();
    return query === "" ? "" : `?${query}`;
}
function dueItemsAbfrage(q) {
    const p = new URLSearchParams({ from: q.from, to: q.to });
    if (q.sources !== undefined)
        p.set("sources", q.sources.join(","));
    for (const name of ["company", "customer", "assignee", "team"]) {
        const wert = q[name];
        if (wert !== undefined)
            p.set(name, wert);
    }
    if (q.linked === true)
        p.set("linked", "true");
    if (q.q !== undefined && q.q !== "")
        p.set("q", q.q);
    if (q.overdue === true)
        p.set("overdue", "include");
    if (q.open === true)
        p.set("open", "true");
    if (q.ticketsMine === true)
        p.set("tickets", "mine");
    return p.toString();
}
/** Abfrage von `GET /api/milestones`: nur gesetzte Teile; Zustände als Komma-Liste (leer = Vorgabe des Servers). */
function milestoneSearchQuery(q) {
    const p = new URLSearchParams();
    if (q.company !== undefined)
        p.set("company", q.company);
    if (q.q !== undefined && q.q !== "")
        p.set("q", q.q);
    if (q.states !== undefined && q.states.length > 0)
        p.set("state", q.states.join(","));
    const text = p.toString();
    return text === "" ? "" : `?${text}`;
}
function ticketFilter(f = {}) {
    const p = new URLSearchParams();
    if (f.company !== undefined)
        p.set("company", f.company);
    if (f.customer !== undefined)
        p.set("customer", f.customer);
    if (f.assignee !== undefined)
        p.set("assignee", f.assignee);
    if (f.team !== undefined)
        p.set("team", f.team);
    if (f.linked === true)
        p.set("linked", "true");
    if (f.archive === true)
        p.set("archive", "true");
    if (f.q !== undefined && f.q !== "")
        p.set("q", f.q);
    if (f.page !== undefined && f.page > 1)
        p.set("page", String(f.page));
    const s = p.toString();
    return s === "" ? "" : `?${s}`;
}
export function createClient(basisAdresse = "", optionen = {}) {
    /** Heißt wie früher die Basisadresse, damit die Wege unten unverändert `basisUrl` reichen. */
    const basisUrl = {
        basis: basisAdresse,
        kopf: () => {
            const id = optionen.aktivesUnternehmen?.() ?? null;
            // Auch eine leere Kennung heißt „kein Kopf" — sie träfe kein Unternehmen.
            return !id ? {} : { "x-active-company": id };
        },
    };
    return {
        getSession: () => hole(basisUrl, "/api/session"),
        getCompanies: () => hole(basisUrl, "/api/companies"),
        getCustomers: (q = {}) => {
            const p = new URLSearchParams();
            if (q.search !== undefined && q.search !== "")
                p.set("search", q.search);
            unternehmenAnhaengen(p, q.companyIds);
            for (const rolle of q.role ?? [])
                p.append("role", rolle);
            if (q.includeFormer !== undefined)
                p.set("includeFormer", String(q.includeFormer));
            if (q.limit !== undefined)
                p.set("limit", String(q.limit));
            if (q.offset !== undefined)
                p.set("offset", String(q.offset));
            const anhang = p.toString();
            return hole(basisUrl, `/api/customers${anhang === "" ? "" : `?${anhang}`}`);
        },
        getCustomer: (id) => hole(basisUrl, `/api/customers/${encodeURIComponent(id)}`),
        updatePreferences: (p) => sende(basisUrl, "/api/preferences", "PUT", p),
        createCustomer: (eingabe) => sendeUndLies(basisUrl, "/api/customers", "POST", eingabe),
        updateCustomer: (id, version, customer) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(id)}`, "PATCH", { version, customer }),
        setCustomerParent: (id, version, parentId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(id)}/parent`, "PATCH", { version, parentId }),
        getKonzern: (id) => hole(basisUrl, `/api/customers/${encodeURIComponent(id)}/konzern`),
        addRelation: (id, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(id)}/relations`, "POST", eingabe),
        findDuplicates: (name) => {
            const p = new URLSearchParams();
            p.set("name", name);
            return hole(basisUrl, `/api/customers/duplicates?${p.toString()}`);
        },
        acquireLock: (ziel, force) => 
        // Der eigentliche Schutz gegen einen zweiten force-Weg steht HIER, nicht im Typ
        // SperrZielKern (siehe dessen Kommentar): force steht bewusst NACH dem Spread von
        // ziel und gewinnt deshalb zur Laufzeit immer — auch dann, wenn ziel über einen
        // vortypisierten Aufrufer trotzdem ein force-Feld mitbringt.
        sendeUndLies(basisUrl, "/api/locks", "POST", { ...ziel, force: force ?? false }),
        refreshLock: (ziel) => sendeUndLies(basisUrl, "/api/locks/refresh", "POST", ziel),
        releaseLock: (ziel) => sende(basisUrl, "/api/locks", "DELETE", ziel),
        lookupEnrichment: (hint) => sendeUndLies(basisUrl, "/api/enrichment/lookup", "POST", hint),
        uploadCustomerLogo: (id, url) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(id)}/logo`, "POST", { url }),
        listSelectableContactRoles: () => hole(basisUrl, "/api/contact-roles"),
        listContacts: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/contacts`),
        createContact: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/contacts`, "POST", eingabe),
        updateContact: (id, version, eingabe) => sendeUndLies(basisUrl, `/api/contacts/${encodeURIComponent(id)}`, "PATCH", { version, ...eingabe }),
        setMainContact: (customerId, contactId) => 
        // Bewusst über sende(): Die Route antwortet mit 204 ohne Rumpf. sendeUndLies() liefe
        // hier in einen UNGUELTIGES_JSON-Fehler, obwohl alles gutgegangen ist.
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/main-contact`, "POST", { contactId }),
        markContactLeft: (id, version, leftOn, nachfolger) => sendeUndLies(basisUrl, `/api/contacts/${encodeURIComponent(id)}/left`, "POST", { version, leftOn, nachfolger: nachfolger ?? null }),
        listNotes: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/notes`),
        createNote: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/notes`, "POST", eingabe),
        updateNote: (customerId, noteId, expectedVersion, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/notes/${encodeURIComponent(noteId)}`, "PUT", { expectedVersion, ...eingabe }),
        setNoteCompanies: (customerId, noteId, companyIds) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/notes/${encodeURIComponent(noteId)}/companies`, "PUT", { companyIds }),
        setFileCompanies: (customerId, fileId, companyIds) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}/companies`, "PUT", { companyIds }),
        deleteNote: (customerId, noteId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/notes/${encodeURIComponent(noteId)}`, "DELETE", undefined),
        listOffers: (customerId, companyIds) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers${sichtAnhang(companyIds)}`),
        createOffer: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers`, "POST", eingabe),
        getOffer: (customerId, offerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`),
        updateOffer: (customerId, offerId, version, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`, "PUT", { version, ...eingabe }),
        setOfferStatus: (customerId, offerId, version, status) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`
            + "/status", "PUT", { version, status }),
        neueOfferVersion: (customerId, offerId, vorlageOfferId, versionNote) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`
            + "/versionen", 
        // Immer ein Objekt, auch ohne Vorlage: Ein POST ganz ohne Rumpf ergäbe an
        // einem Rumpfschema eine 400 über den Inhaltstyp statt über die Sache.
        "POST", { vorlageOfferId, ...(versionNote === undefined ? {} : { versionNote }) }),
        kopiereOffer: (customerId, offerId, replacesSource) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`
            + "/kopie", "POST", replacesSource === undefined ? {} : { replacesSource }),
        setOfferReplaces: (customerId, offerId, replacesOfferId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}/replaces`, "PUT", { replacesOfferId }),
        createHistoricalOffer: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/historical`, "POST", eingabe),
        updateHistoricalOffer: (customerId, offerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}/historical`, "PUT", eingabe),
        createHistoricalOrder: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/historical`, "POST", eingabe),
        updateHistoricalOrder: (customerId, orderId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/${encodeURIComponent(orderId)}/historical`, "PUT", eingabe),
        listDocumentFiles: (customerId, ref) => hole(basisUrl, vorgangsDateiPfad(customerId, ref)),
        uploadDocumentFiles: (customerId, ref, dateien, optionen = {}) => {
            const form = new FormData();
            // folderId/fileTypeId VOR den Datei-Teilen — die Route prüft sie vor dem ersten Teil (wie uploadFile).
            if (optionen.folderId !== undefined)
                form.append("folderId", optionen.folderId);
            if (optionen.fileTypeId !== undefined)
                form.append("fileTypeId", optionen.fileTypeId);
            for (const datei of dateien)
                form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, vorgangsDateiPfad(customerId, ref), form);
        },
        linkDocumentFile: async (customerId, ref, fileId) => 
        // Ohne Rumpf: Die Kennung steht im Pfad; 201 und 200 tragen das Ergebnis im Rumpf.
        (await sendeUndLies(basisUrl, vorgangsDateiPfad(customerId, ref, fileId), "PUT", undefined))
            .result,
        unlinkDocumentFile: (customerId, ref, fileId, optionen = {}) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, vorgangsDateiPfad(customerId, ref, fileId) + abfrage({ delete: optionen.delete }), "DELETE", undefined),
        listOfferVersions: (customerId, offerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`
            + "/versionen"),
        listSalesOrders: (customerId, companyIds) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders${sichtAnhang(companyIds)}`),
        createSalesOrder: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders`, "POST", eingabe),
        getSalesOrder: (customerId, orderId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/${encodeURIComponent(orderId)}`),
        updateSalesOrder: (customerId, orderId, version, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/${encodeURIComponent(orderId)}`, "PUT", { version, ...eingabe }),
        setSalesOrderStatus: (customerId, orderId, version, status) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/${encodeURIComponent(orderId)}`
            + "/status", "PUT", { version, status }),
        listInvoices: (customerId, companyIds) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/invoices${sichtAnhang(companyIds)}`),
        getInvoice: (customerId, invoiceId) => hole(basisUrl, rechnungsPfad(customerId, invoiceId)),
        updateInvoice: (customerId, invoiceId, version, eingabe) => sendeUndLies(basisUrl, rechnungsPfad(customerId, invoiceId), "PATCH", { version, ...eingabe }),
        createInvoiceFromOrder: (customerId, orderId, choice) => 
        // Ohne Wahl OHNE Rumpf (wie bisher): Die Route braucht dann nichts ausser den beiden Kennungen im Pfad — ein leeres
        // Objekt wäre ein Rumpf, den niemand liest. Mit Wahl geht sie als JSON hinaus.
        sendeUndLies(basisUrl, auftragsWeg(customerId, orderId, "rechnung-erzeugen"), "POST", choice),
        getInvoiceFromOrderPreview: (customerId, orderId) => hole(basisUrl, auftragsWeg(customerId, orderId, "rechnung-erzeugen")),
        issueInvoice: (customerId, invoiceId, version, invoiceDate = null) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/festschreiben`, "POST", 
        // `invoiceDate` steht IMMER im Rumpf, auch als null: Der Vertrag unterscheidet
        // „heute" (null) nicht von „weggelassen" — beides bedeutet dasselbe —, aber ein
        // ausdrücklicher Wert liest sich im Netzwerkprotokoll besser als ein fehlendes Feld
        // (dieselbe Entscheidung wie bei `beiSortIndex`).
        { version, invoiceDate }),
        cancelInvoice: (customerId, invoiceId, version) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/stornieren`, "POST", { version }),
        markInvoicePaid: (customerId, invoiceId, version, paidAt = null) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/als-bezahlt`, "POST", 
        // `paidAt` steht IMMER im Rumpf, auch als null — dieselbe Entscheidung und
        // dieselbe Begründung wie bei `invoiceDate` in `issueInvoice`.
        { version, paidAt }),
        unmarkInvoicePaid: (customerId, invoiceId, version) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/bezahlt-zuruecknehmen`, "POST", { version }),
        createInvoiceKorrektur: async (customerId, invoiceId) => {
            // 201 = eben angelegt, 200 = es gab schon eine (siehe `sendeUndLiesMitCode`).
            const { status, daten } = await sendeUndLiesMitCode(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/korrektur-erzeugen`, "POST", undefined);
            return { korrektur: daten, bestand: status === 200 };
        },
        invoiceRenderModel: (customerId, invoiceId) => hole(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/rendermodell`),
        renderInvoice: (customerId, invoiceId, profil = "PDF_A3") => sendeBinaer(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/rendern`
            // Getippt und aus der erzeugten Aufzählung — hier kann nichts anderes stehen;
            // die Kodierung bleibt trotzdem, weil ein Pfadbau ohne sie zum Muster würde.
            + `?profil=${encodeURIComponent(profil)}`),
        vorschauInvoice: (customerId, invoiceId, dpi) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/vorschau`, "POST", vorschauRumpf(dpi)),
        getInvoiceAblage: (customerId, invoiceId) => hole(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/ablage`),
        createInvoiceAblage: (customerId, invoiceId) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/in-akte-ablegen`, 
        // OHNE Rumpf, wie `createInvoiceFromOrder`: Die Route braucht nichts ausser den
        // beiden Kennungen im Pfad — was abgelegt wird, steht an der Rechnung.
        "POST", undefined),
        offerRenderModel: (customerId, offerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/rendermodell`),
        renderOffer: (customerId, offerId) => sendeBinaer(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/rendern`),
        vorschauOffer: (customerId, offerId, dpi) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/vorschau`, "POST", vorschauRumpf(dpi)),
        getOfferAblage: (customerId, offerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/ablage`),
        createOfferAblage: (customerId, offerId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/in-akte-ablegen`, 
        // OHNE Rumpf, wie `createInvoiceAblage`: Die Route braucht nichts ausser den
        // beiden Kennungen im Pfad — was abgelegt wird, steht am Angebot.
        "POST", undefined),
        orderRenderModel: (customerId, orderId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/`
            + `${encodeURIComponent(orderId)}/rendermodell`),
        renderOrder: (customerId, orderId) => sendeBinaer(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/`
            + `${encodeURIComponent(orderId)}/rendern`),
        vorschauOrder: (customerId, orderId, dpi) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/`
            + `${encodeURIComponent(orderId)}/vorschau`, "POST", vorschauRumpf(dpi)),
        listItems: (bezug) => hole(basisUrl, positionenPfad(bezug)),
        createItem: (bezug, version, eingabe, beiSortIndex = null) => sendeUndLies(basisUrl, positionenPfad(bezug), "POST", 
        // `beiSortIndex` steht IMMER im Rumpf, auch als null: Der Vertrag unterscheidet
        // „ans Ende" (null) nicht von „weggelassen" — beides bedeutet dasselbe —, aber ein
        // ausdrücklicher Wert liest sich im Netzwerkprotokoll besser als ein fehlendes Feld.
        { version, beiSortIndex, ...eingabe }),
        updateItem: (bezug, itemId, version, eingabe) => sendeUndLies(basisUrl, `${positionenPfad(bezug)}/${encodeURIComponent(itemId)}`, "PATCH", { version, ...eingabe }),
        deleteItem: (bezug, itemId, version) => 
        // sendeUndLies(), nicht sende(): Die Antwort trägt den neuen Stand (kein 204). Der
        // Rumpf bleibt dabei LEER (`undefined` → JSON.stringify liefert undefined → fetch
        // sendet keinen) und die Version geht als Abfrageparameter hinaus — Zwischenserver
        // dürfen den Rumpf einer DELETE-Anfrage verwerfen (Ruling P3B1B-F).
        sendeUndLies(basisUrl, `${positionenPfad(bezug)}/${encodeURIComponent(itemId)}`
            + `?version=${encodeURIComponent(String(version))}`, "DELETE", undefined),
        reorderItems: (bezug, version, geordneteIds) => sendeUndLies(basisUrl, `${positionenPfad(bezug)}/reihenfolge`, "POST", { version, geordneteIds }),
        resetItem: (bezug, itemId, version) => sendeUndLies(basisUrl, `${positionenPfad(bezug)}/${encodeURIComponent(itemId)}/reset`, "POST", { version }),
        applyTemplate: (customerId, offerId, version, vorlageId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/vorlage-anwenden`, "POST", { version, vorlageId }),
        listPlaceholders: () => hole(basisUrl, "/api/platzhalter"),
        searchProducts: (q = {}) => {
            // Bauart wie listProducts: nur gesetzte Filter landen in der Abfragezeichenkette. Ein
            // leerer Text wäre sonst ein Filter auf die leere Zeichenkette und träfe nichts.
            // `nurAktive` fehlt hier absichtlich — der Server brennt es ein (Ruling P3B1B-G) und
            // wiese ein mitgeschicktes Feld mit 400 ab.
            const p = new URLSearchParams();
            unternehmenAnhaengen(p, q.companyIds);
            if (q.category !== undefined && q.category !== "")
                p.set("category", q.category);
            if (q.suche !== undefined && q.suche !== "")
                p.set("suche", q.suche);
            if (q.limit !== undefined)
                p.set("limit", String(q.limit));
            if (q.offset !== undefined)
                p.set("offset", String(q.offset));
            const anhang = p.toString();
            return hole(basisUrl, `/api/artikel${anhang === "" ? "" : `?${anhang}`}`);
        },
        listActiveTaxRates: () => hole(basisUrl, "/api/steuersaetze"),
        listTemplates: (companyId) => 
        // `companyId` ist PFLICHT (anders als bei searchProducts) und steht deshalb immer in
        // der Abfrage: Eine Vorlagenliste ohne Unternehmen wäre eine, aus der man nichts
        // anwenden darf — der Server weist sie mit 400 ab.
        hole(basisUrl, `/api/angebotsvorlagen?companyId=${encodeURIComponent(companyId)}`),
        searchInvoices: (q = {}) => hole(basisUrl, `/api/invoices${rechnungsSucheAnhang(q)}`),
        searchOffers: (q = {}) => hole(basisUrl, `/api/offers${vorgangsSucheAnhang(q)}`),
        searchOrders: (q = {}) => hole(basisUrl, `/api/orders${vorgangsSucheAnhang(q)}`),
        getOfferById: (offerId) => hole(basisUrl, `/api/offers/${encodeURIComponent(offerId)}`),
        getOrderById: (orderId) => hole(basisUrl, `/api/orders/${encodeURIComponent(orderId)}`),
        interfaceStatus: () => hole(basisUrl, "/api/schnittstellen/status"),
        aircallStatus: () => hole(basisUrl, "/api/aircall/status"),
        aircallKlaerliste: (q = {}) => hole(basisUrl, `/api/aircall/klaerliste${klaerlisteAnhang(q)}`),
        aircallZuordnen: (eingabe) => sendeUndLies(basisUrl, "/api/aircall/klaerliste/zuordnen", "POST", eingabe),
        aircallIgnorieren: (nummer) => sendeUndLies(basisUrl, "/api/aircall/klaerliste/ignorieren", "POST", { nummer }),
        aircallLeitungen: () => hole(basisUrl, "/api/aircall/leitungen"),
        aircallLeitungsAnrufe: (leitung, q = {}) => hole(basisUrl, `/api/aircall/leitungen/anrufe${abfrage({ leitung, ...q })}`),
        aircallKeinemKunden: (leitungId, eingabe) => sendeUndLies(basisUrl, `/api/aircall/leitungen/${encodeURIComponent(leitungId)}/keinem-kunden`, "POST", eingabe),
        aircallMengen: (q) => hole(basisUrl, `/api/aircall/mengen${abfrage(q)}`),
        aircallEinstellungen: () => hole(basisUrl, "/api/aircall/einstellungen"),
        setzeAircallEinstellungen: (eingabe) => sendeUndLies(basisUrl, "/api/aircall/einstellungen", "PUT", eingabe),
        aircallAnbindungLeitungen: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account/leitungen`),
        ordneAircallLeitungZu: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account/leitungen`, "POST", eingabe),
        beendeAircallLeitung: (customerId, leitungId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account/leitungen/`
            + `${encodeURIComponent(leitungId)}/ende`, "POST", eingabe),
        widerrufeAircallZuordnung: (customerId, zuordnungId, version) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account/zuordnungen/`
            + `${encodeURIComponent(zuordnungId)}?version=${version}`, "DELETE", undefined),
        aircallAuswirkung: (customerId, leitungId, q) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account/leitungen/`
            + `${encodeURIComponent(leitungId)}/auswirkung${abfrage(q)}`),
        listImportantNotes: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/important-notes`),
        createImportantNote: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/important-notes`, "POST", eingabe),
        updateImportantNote: (customerId, noteId, version, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/important-notes/`
            + `${encodeURIComponent(noteId)}`, "PUT", { version, ...eingabe }),
        deleteImportantNote: (customerId, noteId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/important-notes/`
            + `${encodeURIComponent(noteId)}`, "DELETE", undefined),
        listOrderRuntimeWarnings: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/order-runtime-warnings`),
        listExternalReferences: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/external-references`),
        createExternalReference: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/external-references`, "POST", eingabe),
        deleteExternalReference: (customerId, referenceId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/external-references/`
            + `${encodeURIComponent(referenceId)}`, "DELETE", undefined),
        getAircallAccount: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account`),
        setzeAircallAccount: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account`, "PUT", eingabe),
        loescheAircallAccount: (customerId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account`, "DELETE", undefined),
        monitorKarte: (customerId, referenceId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/external-references/`
            + `${encodeURIComponent(referenceId)}/monitor`),
        listAddresses: (besitzer, ownerId) => hole(basisUrl, adressWeg(besitzer, ownerId)),
        createAddress: (besitzer, ownerId, eingabe) => sendeUndLies(basisUrl, adressWeg(besitzer, ownerId), "POST", eingabe),
        updateAddress: (besitzer, ownerId, addressId, eingabe) => sendeUndLies(basisUrl, `${adressWeg(besitzer, ownerId)}/${encodeURIComponent(addressId)}`, "PATCH", eingabe),
        setPrimaryAddress: (besitzer, ownerId, addressId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `${adressWeg(besitzer, ownerId)}/${encodeURIComponent(addressId)}/primary`, "POST", undefined),
        archiveAddress: (besitzer, ownerId, addressId) => sende(basisUrl, `${adressWeg(besitzer, ownerId)}/${encodeURIComponent(addressId)}`, "DELETE", undefined),
        getBelegParteien: (art, customerId, vorgangId) => hole(basisUrl, parteienPfad(customerId, art, vorgangId)),
        setBelegParteien: (art, customerId, vorgangId, eingabe) => sendeUndLies(basisUrl, parteienPfad(customerId, art, vorgangId), "PUT", eingabe),
        getBelegLayout: (art, customerId, vorgangId) => hole(basisUrl, belegLayoutPfad(customerId, art, vorgangId)),
        setBelegLayout: (art, customerId, vorgangId, eingabe) => sendeUndLies(basisUrl, belegLayoutPfad(customerId, art, vorgangId), "PUT", eingabe),
        getZahlplan: (bezug) => hole(basisUrl, zahlplanPfad(bezug)),
        setZahlplan: (bezug, eingabe) => sendeUndLies(basisUrl, zahlplanPfad(bezug), "PUT", eingabe),
        getFaelligkeiten: (customerId, orderId) => hole(basisUrl, faelligkeitenPfad(customerId, orderId)),
        berechneFaelligkeiten: (customerId, orderId) => sendeUndLies(basisUrl, `${faelligkeitenPfad(customerId, orderId)}/neu-berechnen`, "POST", {}),
        getAbrechnungsquellen: (customerId, orderId) => hole(basisUrl, auftragsWeg(customerId, orderId, "abrechnungsquellen")),
        setzeAbrechnungsquelle: (customerId, orderId, source, eingabe) => sendeUndLies(basisUrl, `${auftragsWeg(customerId, orderId, "abrechnungsquellen")}/${encodeURIComponent(source)}`, "PUT", eingabe),
        loeseAbrechnungsquelle: (customerId, orderId, source, version) => sendeUndLies(basisUrl, `${auftragsWeg(customerId, orderId, "abrechnungsquellen")}/${encodeURIComponent(source)}`
            + `?version=${String(version)}`, "DELETE", undefined),
        getAnhaenge: (bezug) => hole(basisUrl, anhangPfad(bezug)),
        anhangAnhaengen: (bezug, version, textBlockId) => sendeUndLies(basisUrl, anhangPfad(bezug), "POST", { version, textBlockId }),
        anhangEntfernen: (bezug, anhangId, version) => sendeUndLies(basisUrl, `${anhangPfad(bezug)}/${encodeURIComponent(anhangId)}`
            + `?version=${String(version)}`, "DELETE", undefined),
        anhaengeOrdnen: (bezug, version, geordneteIds) => sendeUndLies(basisUrl, `${anhangPfad(bezug)}/reihenfolge`, "PUT", { version, geordneteIds }),
        anhangSichtbarkeit: (customerId, orderId, anhangId, version, hidden) => sendeUndLies(basisUrl, `${anhangPfad({ art: "order", customerId, vorgangId: orderId })}`
            + `/${encodeURIComponent(anhangId)}`, "PATCH", { version, hidden }),
        getRechnungsvorschlag: (customerId, orderId, stichtag) => hole(basisUrl, auftragsWeg(customerId, orderId, "rechnungsvorschlag")
            + (stichtag === undefined ? "" : `?stichtag=${encodeURIComponent(stichtag)}`)),
        erzeugeRechnungAusVorschlag: (customerId, orderId, eingabe) => sendeUndLies(basisUrl, auftragsWeg(customerId, orderId, "rechnungsvorschlag"), "POST", eingabe),
        getAbrechnung: (customerId, orderId, periodStart) => hole(basisUrl, auftragsWeg(customerId, orderId, "abrechnung")
            + (periodStart === undefined
                ? "" : `?jahr=${encodeURIComponent(periodStart)}`)),
        getVolumen: (customerId, orderId, jahr) => hole(basisUrl, auftragsWeg(customerId, orderId, "volumen")
            + (jahr === undefined ? "" : `?jahr=${jahr}`)),
        uploadFile: (customerId, datei, optionen = {}) => {
            const form = new FormData();
            // folderId/fileTypeId VOR dem Datei-Teil anhängen — nur so liegen sie serverseitig an
            // daten.fields (der Handler liest sie vor dem Stream).
            if (optionen.folderId !== undefined)
                form.append("folderId", optionen.folderId);
            if (optionen.fileTypeId !== undefined)
                form.append("fileTypeId", optionen.fileTypeId);
            form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files`, form);
        },
        downloadFile: (customerId, fileId) => holeBinaer(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}`),
        listFiles: (customerId, filter = {}) => {
            const p = new URLSearchParams();
            // null => "null" (Wurzel/unkategorisiert), ein Wert => der Wert, undefined => weglassen.
            if (filter.folderId !== undefined)
                p.set("folderId", filter.folderId ?? "null");
            if (filter.fileTypeId !== undefined)
                p.set("fileTypeId", filter.fileTypeId ?? "null");
            const anhang = p.toString();
            return hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files${anhang === "" ? "" : `?${anhang}`}`);
        },
        renameFile: (customerId, fileId, name) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}`, "PATCH", { originalName: name }),
        moveFile: (customerId, fileId, folderId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}`, "PATCH", { folderId }),
        setFileType: (customerId, fileId, fileTypeId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}`, "PATCH", { fileTypeId }),
        deleteFile: (customerId, fileId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}`, "DELETE", undefined),
        listFolders: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/folders`),
        createFolder: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/folders`, "POST", eingabe),
        renameFolder: (customerId, folderId, name) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/folders/${encodeURIComponent(folderId)}`, "PATCH", { name }),
        moveFolder: (customerId, folderId, newParentId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/folders/${encodeURIComponent(folderId)}`, "PATCH", { parentId: newParentId }),
        deleteFolder: (customerId, folderId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/folders/${encodeURIComponent(folderId)}`, "DELETE", undefined),
        listSelectablePaymentTerms: () => hole(basisUrl, "/api/payment-terms"),
        listLaender: () => hole(basisUrl, "/api/laender"),
        listPlzOrte: (plz) => hole(basisUrl, `/api/postleitzahlen/${encodeURIComponent(plz)}`),
        getRelationTerms: (relationId) => hole(basisUrl, `/api/relations/${encodeURIComponent(relationId)}/terms`),
        setRelationTerms: (relationId, version, terms) => sendeUndLies(basisUrl, `/api/relations/${encodeURIComponent(relationId)}/terms`, "PATCH", { version, terms }),
        getRelationBilling: (relationId) => hole(basisUrl, `/api/relations/${encodeURIComponent(relationId)}/billing`),
        setRelationBilling: (relationId, eingabe) => sendeUndLies(basisUrl, `/api/relations/${encodeURIComponent(relationId)}/billing`, "PUT", eingabe),
        listUsers: () => hole(basisUrl, "/api/verwaltung/users"),
        setUserRole: (id, version, role, homeCompanyId, phone) => sendeUndLies(basisUrl, `/api/verwaltung/users/${encodeURIComponent(id)}`, "PATCH", { version, role, homeCompanyId, ...(phone === undefined ? {} : { phone }) }),
        setUserSignature: (id, datei) => {
            const form = new FormData();
            form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, `/api/verwaltung/users/${encodeURIComponent(id)}/unterschrift`, form, "PUT");
        },
        deleteUserSignature: (id) => sendeUndLies(basisUrl, `/api/verwaltung/users/${encodeURIComponent(id)}/unterschrift`, "DELETE", undefined),
        userSignatureUrl: (id, stempel) => `${basisUrl.basis}/api/verwaltung/users/${encodeURIComponent(id)}/unterschrift`
            + (stempel === undefined ? "" : `?v=${stempel}`),
        listUserJobTitles: (id) => hole(basisUrl, `/api/verwaltung/users/${encodeURIComponent(id)}/funktionen`),
        setUserJobTitles: (id, items) => sendeUndLies(basisUrl, `/api/verwaltung/users/${encodeURIComponent(id)}/funktionen`, "PUT", { items }),
        listLayouts: () => hole(basisUrl, "/api/verwaltung/layouts"),
        getLayout: (id) => hole(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}`),
        saveLayoutDraft: (id, version, entwurf) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/entwurf`, "PUT", { version, entwurf }),
        previewLayout: (id, anfrage) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/vorschau`, "POST", anfrage),
        listLayoutRealDocuments: (id) => hole(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/echte-belege`),
        getLayoutCapabilities: () => hole(basisUrl, "/api/verwaltung/layouts/faehigkeiten"),
        createLayoutGraphic: (id, felder, datei) => {
            const form = new FormData();
            form.append("felder", JSON.stringify(felder));
            if (datei !== null)
                form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/grafiken`, form);
        },
        updateLayoutGraphic: (id, gid, felder) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}`
            + `/grafiken/${encodeURIComponent(gid)}`, "PUT", felder),
        deleteLayoutGraphic: (id, gid, version) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}`
            + `/grafiken/${encodeURIComponent(gid)}?version=${version}`, "DELETE", undefined),
        reorderLayoutGraphics: (id, version, geordneteIds) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/grafiken/reihenfolge`, "PUT", { version, geordneteIds }),
        publishLayout: (id, version) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/freigabe`, "POST", { version }),
        discardLayout: (id, version) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/verwerfen`, "POST", { version }),
        adoptLayoutStore: (id, version) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/ablage-uebernehmen`, "POST", { version }),
        layoutGraphicUrl: (id, gid, stempel) => `${basisUrl.basis}/api/verwaltung/layouts/${encodeURIComponent(id)}/grafiken/`
            + `${encodeURIComponent(gid)}/svg${stempel === undefined ? "" : `?v=${stempel}`}`,
        layoutGraphicImageUrl: (id, gid, stempel) => `${basisUrl.basis}/api/verwaltung/layouts/${encodeURIComponent(id)}/grafiken/`
            + `${encodeURIComponent(gid)}/bild${stempel === undefined ? "" : `?v=${stempel}`}`,
        copyLayout: (id, name) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/kopie`, "POST", { name }),
        renameLayout: (id, version, name) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/name`, "PUT", { version, name }),
        setDefaultLayout: (id, version) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/vorgabe`, "POST", { version }),
        archiveLayout: (id, version, archiviert) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/archiv`, "POST", { version, archiviert }),
        deleteLayout: (id, version) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}?version=${version}`, "DELETE", undefined),
        listNumberRanges: () => hole(basisUrl, "/api/verwaltung/number-ranges"),
        updateNumberRangePattern: (id, expectedUpdatedAt, pattern) => sendeUndLies(basisUrl, `/api/verwaltung/number-ranges/${encodeURIComponent(id)}`, "PATCH", { expectedUpdatedAt, pattern }),
        setNumberRangeStart: (id, expectedUpdatedAt, startValue) => 
        // Nur diese beiden Felder — der Startwert-Zweig des Schemas verträgt kein `pattern`
        // daneben (dann 400).
        sendeUndLies(basisUrl, `/api/verwaltung/number-ranges/${encodeURIComponent(id)}`, "PATCH", { expectedUpdatedAt, startValue }),
        listPaymentTermsVerwaltung: () => hole(basisUrl, "/api/verwaltung/payment-terms"),
        createPaymentTerm: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/payment-terms", "POST", eingabe),
        updatePaymentTerm: (id, version, eingabe) => sendeUndLies(basisUrl, `/api/verwaltung/payment-terms/${encodeURIComponent(id)}`, "PATCH", { version, ...eingabe }),
        archivePaymentTerm: (id, version) => 
        // Nur diese beiden Felder — der Archivierzweig des Schemas verträgt kein weiteres
        // (dann 400), und die Antwort kommt als 204 ohne Rumpf.
        sende(basisUrl, `/api/verwaltung/payment-terms/${encodeURIComponent(id)}`, "PATCH", { version, archiviert: true }),
        listContactRoles: () => hole(basisUrl, "/api/verwaltung/contact-roles"),
        getAnsprechpartner: (bezug) => hole(basisUrl, ansprechpartnerPfad(bezug)),
        setAnsprechpartner: (bezug, eingabe) => sendeUndLies(basisUrl, ansprechpartnerPfad(bezug), "PUT", eingabe),
        listAnsprechpartnerKategorien: (companyId) => hole(basisUrl, `${KATEGORIEN_PFAD}${companyId === undefined ? "" : `?companyId=${encodeURIComponent(companyId)}`}`),
        createAnsprechpartnerKategorie: (eingabe) => sendeUndLies(basisUrl, KATEGORIEN_PFAD, "POST", eingabe),
        updateAnsprechpartnerKategorie: (id, eingabe) => sendeUndLies(basisUrl, `${KATEGORIEN_PFAD}/${encodeURIComponent(id)}`, "PATCH", eingabe),
        deleteAnsprechpartnerKategorie: (id) => sende(basisUrl, `${KATEGORIEN_PFAD}/${encodeURIComponent(id)}`, "DELETE", undefined),
        createContactRole: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/contact-roles", "POST", eingabe),
        updateContactRole: (id, version, eingabe) => sendeUndLies(basisUrl, `/api/verwaltung/contact-roles/${encodeURIComponent(id)}`, "PATCH", { version, ...eingabe }),
        listCompaniesMaster: () => hole(basisUrl, "/api/verwaltung/unternehmen"),
        updateCompanyMaster: (id, eingabe) => 
        // Ohne `version` (last-write-wins): der Rumpf ist der volle Sieben-Feld-Satz der
        // Stammdaten, wahlweise um name/colorHex ergänzt.
        sendeUndLies(basisUrl, `/api/verwaltung/unternehmen/${encodeURIComponent(id)}`, "PATCH", eingabe),
        createCompany: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/unternehmen", "POST", eingabe),
        setCompanyActive: (id, aktiv) => sendeUndLies(basisUrl, `/api/verwaltung/unternehmen/${encodeURIComponent(id)}/stilllegen`, "POST", { aktiv }),
        setCompanyFooterBlocks: (id, fussbloecke) => sendeUndLies(basisUrl, `/api/verwaltung/unternehmen/${encodeURIComponent(id)}/fussbloecke`, "PUT", { fussbloecke }),
        uploadCompanyLogo: (id, variante, datei) => {
            const form = new FormData();
            form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, verwaltungsLogoPfad(id, variante), form);
        },
        deleteCompanyLogo: (id, variante) => sendeUndLies(basisUrl, verwaltungsLogoPfad(id, variante), "DELETE", undefined),
        listFileTypes: () => hole(basisUrl, "/api/verwaltung/dateitypen"),
        createFileType: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/dateitypen", "POST", eingabe),
        updateFileType: (id, eingabe) => 
        // Ohne `version` (Ruling P4-B): der Rumpf ist genau der volle Feldsatz.
        sendeUndLies(basisUrl, `/api/verwaltung/dateitypen/${encodeURIComponent(id)}`, "PATCH", eingabe),
        setFileTypeActive: (id, active) => 
        // Der Toggle-Rumpf trägt AUSSER `aktiv` kein Feld (sonst 400) und antwortet mit dem
        // geänderten Satz — daher sendeUndLies, nicht sende.
        sendeUndLies(basisUrl, `/api/verwaltung/dateitypen/${encodeURIComponent(id)}`, "PATCH", { aktiv: active }),
        listTaxRates: () => hole(basisUrl, "/api/verwaltung/steuersaetze"),
        createTaxRate: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/steuersaetze", "POST", eingabe),
        updateTaxRate: (id, eingabe) => 
        // Ohne `version` (last-write-wins): der Rumpf ist genau der volle Feldsatz.
        sendeUndLies(basisUrl, `/api/verwaltung/steuersaetze/${encodeURIComponent(id)}`, "PATCH", eingabe),
        setTaxRateActive: (id, active) => sendeUndLies(basisUrl, `/api/verwaltung/steuersaetze/${encodeURIComponent(id)}`, "PATCH", { aktiv: active }),
        listProducts: (q = {}) => {
            // Bauart wie getCustomers: nur gesetzte Filter landen in der Abfragezeichenkette.
            // Ein leerer Text wäre sonst ein Filter auf die leere Zeichenkette und träfe nichts.
            const p = new URLSearchParams();
            if (q.companyId !== undefined && q.companyId !== "")
                p.set("companyId", q.companyId);
            if (q.category !== undefined && q.category !== "")
                p.set("category", q.category);
            if (q.suche !== undefined && q.suche !== "")
                p.set("suche", q.suche);
            if (q.nurAktive === true)
                p.set("nurAktive", "true");
            if (q.limit !== undefined)
                p.set("limit", String(q.limit));
            if (q.offset !== undefined)
                p.set("offset", String(q.offset));
            const anhang = p.toString();
            return hole(basisUrl, `/api/verwaltung/artikel${anhang === "" ? "" : `?${anhang}`}`);
        },
        createProduct: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/artikel", "POST", eingabe),
        updateProduct: (id, eingabe) => 
        // Ohne `version` (last-write-wins): der Rumpf ist genau der volle Feldsatz.
        sendeUndLies(basisUrl, `/api/verwaltung/artikel/${encodeURIComponent(id)}`, "PATCH", eingabe),
        setProductActive: (id, active) => sendeUndLies(basisUrl, `/api/verwaltung/artikel/${encodeURIComponent(id)}`, "PATCH", { aktiv: active }),
        listTextBlocks: (q = {}) => {
            // Bauart wie listProducts: Ein weggelassener (oder leerer) Filter erscheint gar nicht
            // erst in der Abfrage — er wäre sonst ein Filter auf die leere Zeichenkette.
            const anhang = q.companyId !== undefined && q.companyId !== ""
                ? `?companyId=${encodeURIComponent(q.companyId)}` : "";
            return hole(basisUrl, `/api/verwaltung/textbausteine${anhang}`);
        },
        createTextBlock: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/textbausteine", "POST", eingabe),
        updateTextBlock: (id, eingabe) => 
        // Ohne `version` (last-write-wins): der Rumpf ist genau der volle Feldsatz — samt
        // `companyId`, denn die Zugehörigkeit ist hier änderbar.
        sendeUndLies(basisUrl, `/api/verwaltung/textbausteine/${encodeURIComponent(id)}`, "PATCH", eingabe),
        deleteTextBlock: (id) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/verwaltung/textbausteine/${encodeURIComponent(id)}`, "DELETE", undefined),
        listOfferTemplates: (q = {}) => {
            // Bauart wie listTextBlocks: Ein weggelassener (oder leerer) Filter erscheint gar
            // nicht erst in der Abfrage — er wäre sonst ein Filter auf die leere Zeichenkette.
            const anhang = q.companyId !== undefined && q.companyId !== ""
                ? `?companyId=${encodeURIComponent(q.companyId)}` : "";
            return hole(basisUrl, `/api/verwaltung/angebotsvorlagen${anhang}`);
        },
        getOfferTemplate: (id) => hole(basisUrl, `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(id)}`),
        createOfferTemplate: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/angebotsvorlagen", "POST", eingabe),
        updateOfferTemplate: (id, eingabe) => 
        // Ohne `version` (last-write-wins): der Rumpf ist genau der Kopf-Feldsatz.
        sendeUndLies(basisUrl, `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(id)}`, "PATCH", eingabe),
        deleteOfferTemplate: (id) => 
        // 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(id)}`, "DELETE", undefined),
        getDatevKopplung: (companyId) => hole(basisUrl, `/api/verwaltung/unternehmen/${encodeURIComponent(companyId)}/datev`),
        setDatevKopplung: (companyId, eingabe) => sendeUndLies(basisUrl, `/api/verwaltung/unternehmen/${encodeURIComponent(companyId)}/datev`, "PUT", eingabe),
        listDatevKopplungen: () => hole(basisUrl, "/api/datev/unternehmen"),
        getDatevAbgleich: (companyId) => hole(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren`),
        einlesenDatevDebitoren: (companyId, datei, standAt) => {
            const form = new FormData();
            // `standAt` VOR dem Datei-Teil (Muster `uploadFile`): Der Handler liest die Felder,
            // bevor er den Datei-Stream öffnet.
            if (standAt !== undefined)
                form.append("standAt", standAt);
            form.append("datei", datei, datei.name);
            return sendeFormular(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren/einlesen`, form);
        },
        verwerfeDatevDebitorenLauf: (companyId, importId) => sende(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren/laeufe/`
            + encodeURIComponent(importId), "DELETE", undefined),
        legeKundeAusDatevDebitorAn: (companyId, debtorNumber) => sendeUndLies(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren/kunde-anlegen`, "POST", { debtorNumber }),
        ignoriereDatevAbgleichZeile: (companyId, ziel, optionen = {}) => sende(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren/ignorieren`, "POST", { ...ziel, ...optionen }),
        exportiereDatevDebitoren: (companyId) => holeBinaer(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren/export`),
        getDatevAusgang: (companyId) => hole(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/ausgang`),
        erzeugeDatevStapel: (companyId, von, bis) => sendeUndLies(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/ausgang/stapel`, "POST", { von, bis }),
        holeDatevStapelPaket: (companyId, batchId) => holeBinaer(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/ausgang/stapel/`
            + `${encodeURIComponent(batchId)}/paket`),
        bestaetigeDatevStapel: (companyId, batchId) => sende(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/ausgang/stapel/`
            + `${encodeURIComponent(batchId)}/bestaetigen`, "POST", undefined),
        getDatevRueckweg: (companyId) => hole(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg`),
        einlesenDatevBuchungen: (companyId, datei, von, bis, standAt) => {
            const form = new FormData();
            form.append("von", von);
            form.append("bis", bis);
            if (standAt !== undefined)
                form.append("standAt", standAt);
            form.append("datei", datei, datei.name);
            return sendeFormular(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg/einlesen`, form);
        },
        verwerfeDatevBuchungsLauf: (companyId, importId) => sende(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg/laeufe/`
            + encodeURIComponent(importId), "DELETE", undefined),
        getDatevZahlungsliste: (companyId, importId) => hole(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg/laeufe/`
            + `${encodeURIComponent(importId)}/zahlungen`),
        datevZahlungUebernehmen: (companyId, bookingId, zuruecknehmen) => sendeUndLies(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg/zahlungen/`
            + encodeURIComponent(bookingId), "POST", zuruecknehmen === undefined ? {} : { zuruecknehmen }),
        getDatevKonten: (companyId, importId) => hole(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg/laeufe/`
            + `${encodeURIComponent(importId)}/konten`),
        getDatevStandZuRechnung: (customerId, invoiceId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/invoices/`
            + `${encodeURIComponent(invoiceId)}/datev`),
        setDebitorennummer: (customerId, relationId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/relations/`
            + `${encodeURIComponent(relationId)}/debitor`, "POST", eingabe),
        // Lieferanten Strang A (Aufgabe 5)
        setRelationRoles: (customerId, relationId, eingabe) => sendeUndLies(basisUrl, `${relationPath(customerId, relationId)}/roles`, "PUT", eingabe),
        setKreditornummer: (customerId, relationId, eingabe) => sendeUndLies(basisUrl, `${relationPath(customerId, relationId)}/creditor`, "PUT", eingabe),
        // Das Board kennt nur seine Filter (Review 9, seit 1e mit `team`) — Archiv, Suche und Seite fallen weg,
        // statt am Board-Schema mit 400 zu scheitern.
        getTicketBoard: (f = {}) => {
            const { archive: _archiv, q: _suche, page: _seite, ...board } = f;
            return hole(basisUrl, `/api/tickets/board${ticketFilter(board)}`);
        },
        listTickets: (f) => hole(basisUrl, `/api/tickets${ticketFilter(f)}`),
        listCustomerTickets: (customerId, archiv) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/tickets${archiv === true ? "?archive=true" : ""}`),
        createTicket: (e) => sendeUndLies(basisUrl, "/api/tickets", "POST", e),
        getTicket: (id) => hole(basisUrl, `/api/tickets/${encodeURIComponent(id)}`),
        updateTicket: (id, version, patch) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}`, "PATCH", { version, ...patch }),
        ablageTicket: (id, ablage) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/ablage`, "POST", ablage),
        listTicketColumns: () => hole(basisUrl, "/api/ticket-columns"),
        createTicketColumn: (e) => sendeUndLies(basisUrl, "/api/ticket-columns", "POST", e),
        updateTicketColumn: (id, version, e) => sendeUndLies(basisUrl, `/api/ticket-columns/${encodeURIComponent(id)}`, "PATCH", { version, ...e }),
        deleteTicketColumn: (id) => sende(basisUrl, `/api/ticket-columns/${encodeURIComponent(id)}`, "DELETE", undefined),
        orderTicketColumns: (ids) => sendeUndLies(basisUrl, "/api/ticket-columns/order", "POST", { ids }),
        listTicketAssignees: (teamId) => hole(basisUrl, teamId === undefined
            ? "/api/tickets/zustaendige" : `/api/tickets/zustaendige?team=${encodeURIComponent(teamId)}`),
        listTeams: () => hole(basisUrl, "/api/verwaltung/teams"),
        createTeam: (e) => sendeUndLies(basisUrl, "/api/verwaltung/teams", "POST", e),
        updateTeam: (id, version, e) => sendeUndLies(basisUrl, `/api/verwaltung/teams/${encodeURIComponent(id)}`, "PATCH", { version, ...e }),
        listMeineTeams: () => hole(basisUrl, "/api/teams/meine"),
        getTicketTimelineRows: () => hole(basisUrl, "/api/tickets/timeline-rows"),
        listDueItems: (q) => hole(basisUrl, `/api/due-items?${dueItemsAbfrage(q)}`),
        getCalendarSetting: () => hole(basisUrl, "/api/calendar/settings"),
        updateCalendarSetting: (version, e) => sendeUndLies(basisUrl, "/api/calendar/settings", "PUT", { version, ...e }),
        rotateCalendarFeedToken: (version) => sendeUndLies(basisUrl, "/api/calendar/feed-token", "POST", { version }),
        deleteCalendarFeedToken: (version) => sendeUndLies(basisUrl, `/api/calendar/feed-token?version=${version}`, "DELETE", undefined),
        listDueTicketSettings: () => hole(basisUrl, "/api/verwaltung/due-ticket-settings"),
        updateDueTicketSetting: (companyId, version, e) => sendeUndLies(basisUrl, `/api/verwaltung/due-ticket-settings/${encodeURIComponent(companyId)}`, "PUT", { version, ...e }),
        getMilestone: (id) => hole(basisUrl, `/api/milestones/${encodeURIComponent(id)}`),
        createMilestone: (e) => sendeUndLies(basisUrl, "/api/milestones", "POST", e),
        updateMilestone: (id, version, patch) => sendeUndLies(basisUrl, `/api/milestones/${encodeURIComponent(id)}`, "PATCH", { version, ...patch }),
        linkMilestoneTicket: (id, ticketId) => sendeUndLies(basisUrl, `/api/milestones/${encodeURIComponent(id)}/tickets`, "POST", { ticketId }),
        unlinkMilestoneTicket: (id, ticketId) => sendeUndLies(basisUrl, `/api/milestones/${encodeURIComponent(id)}/tickets/${encodeURIComponent(ticketId)}`, "DELETE", undefined),
        searchMilestones: (q) => hole(basisUrl, `/api/milestones${milestoneSearchQuery(q)}`),
        listTicketMilestones: (ticketId) => hole(basisUrl, `/api/tickets/${encodeURIComponent(ticketId)}/milestones`),
        listNumberingSchemes: () => hole(basisUrl, "/api/verwaltung/nummerierungen"),
        createNumberingScheme: (e) => sendeUndLies(basisUrl, "/api/verwaltung/nummerierungen", "POST", e),
        updateNumberingScheme: (id, version, e) => sendeUndLies(basisUrl, `/api/verwaltung/nummerierungen/${encodeURIComponent(id)}`, "PATCH", { version, ...e }),
        deleteNumberingScheme: (id, version) => sende(basisUrl, `/api/verwaltung/nummerierungen/${encodeURIComponent(id)}?version=${version}`, "DELETE", undefined),
        probeNumberingScheme: (formate) => sendeUndLies(basisUrl, "/api/verwaltung/nummerierungen/probe", "POST", { formate }),
        setLayoutNumbering: (id, version, nummerierungId) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/nummerierung`, "PUT", { version, nummerierungId }),
        addTicketCustomer: (t, customerId) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/customers`, "POST", { customerId }),
        previewRemoveTicketCustomer: (t, c) => hole(basisUrl, `/api/tickets/${encodeURIComponent(t)}/customers/${encodeURIComponent(c)}/vorschau`),
        removeTicketCustomer: (t, c) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/customers/${encodeURIComponent(c)}`, "DELETE", undefined),
        addTicketContact: (t, contactId) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/contacts`, "POST", { contactId }),
        removeTicketContact: (t, c) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/contacts/${encodeURIComponent(c)}`, "DELETE", undefined),
        createSubtask: (t, e) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/subtasks`, "POST", e),
        setTicketDone: (id, done, version) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/done`, "POST", { done, version }),
        withdrawSubtask: (id) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}`, "DELETE", undefined),
        listFocusCards: (id) => hole(basisUrl, `/api/tickets/${encodeURIComponent(id)}/subtasks`),
        listOwnerCandidates: (q) => {
            const p = new URLSearchParams();
            if (q.ticket !== undefined)
                p.set("ticket", q.ticket);
            if (q.team !== undefined)
                p.set("team", q.team);
            const s = p.toString();
            return hole(basisUrl, `/api/tickets/owner-candidates${s === "" ? "" : `?${s}`}`);
        },
        searchParentCandidates: (t, q) => hole(basisUrl, `/api/tickets/parent-candidates?${new URLSearchParams({ ticket: t, q }).toString()}`),
        addTicketComment: (t, contentMarkdown) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/comments`, "POST", { contentMarkdown }),
        updateTicketComment: (t, k, version, contentMarkdown) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/comments/${encodeURIComponent(k)}`, "PATCH", { version, contentMarkdown }),
        deleteTicketComment: (t, k) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/comments/${encodeURIComponent(k)}`, "DELETE", undefined),
        listTicketWorkEntries: (t) => hole(basisUrl, `/api/tickets/${encodeURIComponent(t)}/work-entries`),
        addTicketWorkEntry: (t, body) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/work-entries`, "POST", body),
        deleteTicketWorkEntry: (t, w) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/work-entries/${encodeURIComponent(w)}`, "DELETE", undefined),
        addTicketReference: (t, kind, targetId) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/references`, "POST", { kind, targetId }),
        removeTicketReference: (t, r) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/references/${encodeURIComponent(r)}`, "DELETE", undefined),
        searchTicketReferenceCandidates: (t, q) => hole(basisUrl, `/api/tickets/${encodeURIComponent(t)}/reference-candidates?q=${encodeURIComponent(q)}`),
        uploadTicketAttachment: (t, datei) => {
            const form = new FormData();
            form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, `/api/tickets/${encodeURIComponent(t)}/attachments`, form);
        },
        deleteTicketAttachment: (t, a) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/attachments/${encodeURIComponent(a)}`, "DELETE", undefined),
        ticketAttachmentUrl: (t, a) => `${basisUrl.basis}/api/tickets/${encodeURIComponent(t)}/attachments/${encodeURIComponent(a)}`,
        addTicketLink: (t, e) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/links`, "POST", e),
        removeTicketLink: (t, l) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/links/${encodeURIComponent(l)}`, "DELETE", undefined),
        refreshTicketLinks: (t) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/links/refresh`, "POST", undefined),
        listTicketLinkInstanzen: (companyId) => hole(basisUrl, `/api/ticket-links/instanzen${companyId === undefined ? "" : `?company=${encodeURIComponent(companyId)}`}`),
        searchTicketLinks: (system, instance, q) => hole(basisUrl, `/api/ticket-links/search?system=${system}&instance=${encodeURIComponent(instance)}&q=${encodeURIComponent(q)}`),
        listFremdkonten: (userId) => hole(basisUrl, `/api/fremdkonten${userId === undefined ? "" : `?user=${encodeURIComponent(userId)}`}`),
        listFremdkontoKandidaten: (system, instance) => hole(basisUrl, `/api/fremdkonten/kandidaten?system=${system}&instance=${encodeURIComponent(instance)}`),
        setFremdkonto: (eingabe) => sendeUndLies(basisUrl, "/api/fremdkonten", "PUT", eingabe),
        removeFremdkonto: (system, instance, userId) => sende(basisUrl, `/api/fremdkonten/${system}/${encodeURIComponent(instance)}${userId === undefined ? "" : `?user=${encodeURIComponent(userId)}`}`, "DELETE", undefined),
        listFremdtickets: (userId) => hole(basisUrl, `/api/fremdtickets${userId === undefined ? "" : `?user=${encodeURIComponent(userId)}`}`),
        getFremdticket: (id) => hole(basisUrl, `/api/fremdtickets/${encodeURIComponent(id)}`),
        setFremdFaelligkeit: (id, dueDate, erwartet) => sendeUndLies(basisUrl, `/api/fremdtickets/${encodeURIComponent(id)}/faelligkeit`, "PUT", { dueDate, erwartet }),
        syncFremdtickets: () => sendeUndLies(basisUrl, "/api/fremdtickets/abgleich", "POST", {}),
        getLabelFilter: (system, key) => hole(basisUrl, `/api/verwaltung/schnittstellen/${system}/${encodeURIComponent(key)}/labels`),
        setLabelFilter: (system, key, labels, erwartet) => sendeUndLies(basisUrl, `/api/verwaltung/schnittstellen/${system}/${encodeURIComponent(key)}/labels`, "PUT", { labels, erwartet }),
        listLabelKandidaten: (system, key) => hole(basisUrl, `/api/verwaltung/schnittstellen/${system}/${encodeURIComponent(key)}/labels/kandidaten`),
        watchTicket: (id) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/watch`, "PUT", undefined),
        unwatchTicket: (id) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/watch`, "DELETE", undefined),
        addWatcher: (id, userId) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/watchers`, "POST", { userId }),
        removeWatcher: (id, userId) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/watchers/${encodeURIComponent(userId)}`, "DELETE", undefined),
        listNotifications: (o = {}) => {
            const p = new URLSearchParams();
            if (o.nurUngelesen === true)
                p.set("nurUngelesen", "true");
            if (o.limit !== undefined)
                p.set("limit", String(o.limit));
            const anhang = p.toString();
            return hole(basisUrl, `/api/notifications${anhang === "" ? "" : `?${anhang}`}`);
        },
        countNotifications: () => hole(basisUrl, "/api/notifications/count"),
        markNotificationsRead: (z) => sendeUndLies(basisUrl, "/api/notifications/read", "POST", z),
        getNotificationSettings: () => hole(basisUrl, "/api/notification-settings"),
        putNotificationSettings: (e) => sendeUndLies(basisUrl, "/api/notification-settings", "PUT", e),
        getPushKey: () => hole(basisUrl, "/api/push/key"),
        listPushDevices: () => hole(basisUrl, "/api/push/subscriptions"),
        registerPush: (abo) => sendeUndLies(basisUrl, "/api/push/subscriptions", "POST", abo),
        removePushDevice: (id) => sende(basisUrl, `/api/push/subscriptions/${encodeURIComponent(id)}`, "DELETE", undefined),
        unregisterPush: (endpoint) => sende(basisUrl, "/api/push/subscriptions", "DELETE", { endpoint }),
        getCustomerOverview: (customerId, companyIds) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/overview${sichtAnhang(companyIds)}`),
        saveTileLayout: (viewKey, layout) => withTimeout(TILE_LAYOUT_TIMEOUT_MS, (signal) => sendeUndLies(basisUrl, `/api/preferences/tile-layouts/${encodeURIComponent(viewKey)}`, "PUT", layout, signal)),
        // Ohne Rumpf, aber MIT Antwortrumpf (die Vorgabe) — deshalb sendeUndLies, nicht sende.
        resetTileLayout: (viewKey) => withTimeout(TILE_LAYOUT_TIMEOUT_MS, (signal) => sendeUndLies(basisUrl, `/api/preferences/tile-layouts/${encodeURIComponent(viewKey)}`, "DELETE", undefined, signal)),
        getCockpit: () => hole(basisUrl, "/api/cockpit"),
        getCockpitTile: (tileKey, query, signal) => hole(basisUrl, `/api/cockpit/tiles/${encodeURIComponent(tileKey)}${cockpitTileQuery(query)}`, signal),
        saveCockpitLayout: (layout) => withTimeout(TILE_LAYOUT_TIMEOUT_MS, (signal) => sendeUndLies(basisUrl, "/api/cockpit/layout", "PUT", layout, signal)),
        // Wie resetTileLayout: ohne Rumpf, aber MIT Antwortrumpf (die Vorgabe).
        resetCockpitLayout: () => withTimeout(TILE_LAYOUT_TIMEOUT_MS, (signal) => sendeUndLies(basisUrl, "/api/cockpit/layout", "DELETE", undefined, signal)),
        // Lieferanten Fundament (Aufgabe 8)
        getBookingAccounts: (q = {}) => hole(basisUrl, `/api/stammdaten/booking-accounts${abfrage(q)}`),
        getCostCentres: (q = {}) => hole(basisUrl, `/api/stammdaten/cost-centres${abfrage(q)}`),
        getSupplierContractTypes: () => hole(basisUrl, "/api/stammdaten/supplier-contract-types"),
        createBookingAccount: (e) => sendeUndLies(basisUrl, "/api/verwaltung/booking-accounts", "POST", e),
        updateBookingAccount: (id, e) => sendeUndLies(basisUrl, `/api/verwaltung/booking-accounts/${encodeURIComponent(id)}`, "PUT", e),
        createCostCentre: (e) => sendeUndLies(basisUrl, "/api/verwaltung/cost-centres", "POST", e),
        updateCostCentre: (id, e) => sendeUndLies(basisUrl, `/api/verwaltung/cost-centres/${encodeURIComponent(id)}`, "PUT", e),
        updateSupplierContractType: (id, e) => sendeUndLies(basisUrl, `/api/verwaltung/supplier-contract-types/${encodeURIComponent(id)}`, "PUT", e),
        // Lieferanten Strang B (Aufgabe 9)
        listSupplierContractTypes: () => hole(basisUrl, "/api/verwaltung/supplier-contract-types"),
        // Lieferanten Strang C (Aufgabe 12)
        getSupplierContracts: (customerId, q = {}) => hole(basisUrl, `${contractPath(customerId)}${sichtAnhang(q.companyIds)}`),
        createSupplierContract: (customerId, e) => sendeUndLies(basisUrl, contractPath(customerId), "POST", e),
        getSupplierContract: (customerId, contractId) => hole(basisUrl, contractPath(customerId, contractId)),
        updateSupplierContract: (customerId, contractId, e) => sendeUndLies(basisUrl, contractPath(customerId, contractId), "PUT", e),
        addContractTerm: (customerId, contractId, e) => sendeUndLies(basisUrl, `${contractPath(customerId, contractId)}/terms`, "POST", e),
        previewContractTerm: (customerId, contractId, term) => sendeUndLies(basisUrl, `${contractPath(customerId, contractId)}/terms/preview`, "POST", { term }),
        deleteContractTerm: (customerId, contractId, termId, version) => sendeUndLies(basisUrl, `${contractPath(customerId, contractId)}/terms/${encodeURIComponent(termId)}?version=${version}`, "DELETE", undefined),
        terminateSupplierContract: (customerId, contractId, e) => sendeUndLies(basisUrl, `${contractPath(customerId, contractId)}/termination`, "POST", e),
        recalculateSupplierCharges: (customerId, contractId) => sendeUndLies(basisUrl, `${contractPath(customerId, contractId)}/recalculate`, "POST", undefined),
        getSupplierCharges: (customerId, contractId, q = {}) => hole(basisUrl, `${contractPath(customerId, contractId)}/charges${abfrage({ open: q.open })}`),
        confirmChargePayment: (customerId, contractId, chargeId, e) => sendeUndLies(basisUrl, `${contractPath(customerId, contractId)}/charges/${encodeURIComponent(chargeId)}/payment`, "PUT", e),
        listSupplierContractsArea: (q = {}) => hole(basisUrl, `/api/supplier-contracts${contractAreaQuery(q)}`),
        // Lieferanten Strang D (Aufgabe 16)
        getIncomingInvoices: (customerId, q = {}) => hole(basisUrl, `${incomingPath(customerId)}${sichtAnhang(q.companyIds)}`),
        createIncomingInvoice: (customerId, e) => sendeUndLies(basisUrl, incomingPath(customerId), "POST", e),
        getIncomingInvoiceFormData: (customerId, q) => hole(basisUrl, `${incomingPath(customerId)}/form-data${abfrage(q)}`),
        getIncomingInvoice: (customerId, incomingInvoiceId) => hole(basisUrl, incomingPath(customerId, incomingInvoiceId)),
        updateIncomingInvoice: (customerId, incomingInvoiceId, e) => sendeUndLies(basisUrl, incomingPath(customerId, incomingInvoiceId), "PUT", e),
        confirmIncomingPayment: (customerId, incomingInvoiceId, e) => sendeUndLies(basisUrl, `${incomingPath(customerId, incomingInvoiceId)}/payment`, "PUT", e),
        cancelIncomingInvoice: (customerId, incomingInvoiceId, e) => sendeUndLies(basisUrl, `${incomingPath(customerId, incomingInvoiceId)}/cancel`, "POST", e),
        listIncomingArea: (q = {}) => hole(basisUrl, `/api/incoming-invoices${incomingAreaQuery(q)}`),
        // Lieferanten Strang E (Aufgabe 18)
        setInvoiceItemAssignment: (customerId, invoiceId, itemId, e) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/invoices/${encodeURIComponent(invoiceId)}/items/`
            + `${encodeURIComponent(itemId)}/assignment`, "PUT", e),
        // Liquidität Stufe 1 (Aufgabe 4)
        getLiquiditySeries: (q) => hole(basisUrl, `/api/liquidity/series${liquiditySeriesAnhang(q)}`),
        getLiquidityPosition: (kind, id, q = {}) => hole(basisUrl, `/api/liquidity/positions/${encodeURIComponent(kind)}/${encodeURIComponent(id)}${liquiditySeriesAnhang(q)}`),
        getScenarios: (q = {}) => hole(basisUrl, `/api/liquidity/scenarios${sichtAnhang(q.companyIds)}`),
        updateScenario: (id, e) => sendeUndLies(basisUrl, `/api/liquidity/scenarios/${encodeURIComponent(id)}`, "PUT", e),
        getLiquidityAccounts: (q = {}) => hole(basisUrl, `/api/liquidity/accounts${sichtAnhang(q.companyIds)}`),
        createLiquidityAccount: (e) => sendeUndLies(basisUrl, "/api/liquidity/accounts", "POST", e),
        updateLiquidityAccount: (id, e) => sendeUndLies(basisUrl, `/api/liquidity/accounts/${encodeURIComponent(id)}`, "PUT", e),
        addAccountBalance: (accountId, e) => sendeUndLies(basisUrl, `/api/liquidity/accounts/${encodeURIComponent(accountId)}/balances`, "POST", e),
        getExpectedPayment: (invoiceId) => hole(basisUrl, `/api/invoices/${encodeURIComponent(invoiceId)}/expected-payment`),
        setExpectedPayment: (invoiceId, e) => sendeUndLies(basisUrl, `/api/invoices/${encodeURIComponent(invoiceId)}/expected-payment`, "PATCH", e),
        // Liquidität Stufe 2, Planpositionen (Aufgabe 8)
        getPlanningItems: (q = {}) => hole(basisUrl, `/api/liquidity/planning-items${planningItemsQuery(q)}`),
        createPlanningItem: (e) => sendeUndLies(basisUrl, "/api/liquidity/planning-items", "POST", e),
        updatePlanningItem: (id, e) => sendeUndLies(basisUrl, `/api/liquidity/planning-items/${encodeURIComponent(id)}`, "PUT", e),
        deletePlanningItem: (id, version) => sende(basisUrl, `/api/liquidity/planning-items/${encodeURIComponent(id)}?version=${version}`, "DELETE", undefined),
        addPlanningItemAmount: (id, e) => sendeUndLies(basisUrl, `/api/liquidity/planning-items/${encodeURIComponent(id)}/amounts`, "POST", e),
        deletePlanningItemAmount: (id, amountId, version) => sendeUndLies(basisUrl, `/api/liquidity/planning-items/${encodeURIComponent(id)}/amounts/${encodeURIComponent(amountId)}?version=${version}`, "DELETE", undefined),
        // Liquidität Stufe 2, Szenarien (Aufgabe 9)
        createScenario: (e) => sendeUndLies(basisUrl, "/api/liquidity/scenarios", "POST", e),
        setBaselineScenario: (id, e) => sendeUndLies(basisUrl, `/api/liquidity/scenarios/${encodeURIComponent(id)}/baseline`, "POST", e),
        deleteScenario: (id, version) => sende(basisUrl, `/api/liquidity/scenarios/${encodeURIComponent(id)}?version=${version}`, "DELETE", undefined),
        saveScenarioPositions: (id, e) => sendeUndLies(basisUrl, `/api/liquidity/scenarios/${encodeURIComponent(id)}/positions`, "PUT", e),
    };
}
//# sourceMappingURL=client.js.map